import React from 'react';
import styled from 'styled-components';
import { LucideIcon } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  subtext?: string;
  icon?: LucideIcon;
  iconColor?: string;
  gradient?: boolean;
  className?: string;
}

const StyledStatCard = styled.div`
  background: ${props => props.theme.card};
  border: 1px solid ${props => props.theme.border};
  border-radius: ${props => props.theme.radius};
  padding: 1rem;
  box-shadow: ${props => props.theme.shadowSoft};
  transition: ${props => props.theme.transitionSmooth};
  position: relative;
  
  &:hover {
    box-shadow: ${props => props.theme.shadowElegant};
    transform: translateY(-2px);
  }
  
  @media (max-width: 640px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100px;
    text-align: center;
  }
`;

const StatIcon = styled.div<{ $color: string }>`
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${props => props.$color}20;
  position: absolute;
  top: 1rem;
  right: 1rem;
  
  @media (max-width: 640px) {
    position: static;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    margin: 0 auto 2px auto;
    order: 1;
  }
`;

const StatTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${props => props.theme.mutedForeground};
  margin-bottom: 4px;
`;

const StatValue = styled.div<{ $gradient: boolean }>`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.theme.foreground};
  margin-bottom: 2px;
  
  ${props => props.$gradient && `
    background: linear-gradient(90deg, hsl(104 98% 38%), hsl(88 100% 50%));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
  `}
  
  @media (max-width: 640px) {
    order: 2;
    margin-bottom: 1px;
    font-size: 1.125rem;
  }
`;

const StatSubtext = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.mutedForeground};
  
  @media (max-width: 640px) {
    order: 3;
    font-size: 0.625rem;
  }
`;

const GradientIcon = styled.div`
  background: linear-gradient(90deg, hsl(104 98% 38%), hsl(88 100% 50%));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`;

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtext,
  icon: Icon,
  iconColor = '#6b7280',
  gradient = false,
  className
}) => {
  return (
    <StyledStatCard className={className}>
      {Icon && (
        <StatIcon $color={iconColor}>
          {gradient ? (
            <GradientIcon>
              <Icon size={16} />
            </GradientIcon>
          ) : (
            <Icon size={16} />
          )}
        </StatIcon>
      )}
      <StatTitle>{title}</StatTitle>
      <StatValue $gradient={gradient}>{value}</StatValue>
      {subtext && <StatSubtext>{subtext}</StatSubtext>}
    </StyledStatCard>
  );
}; 